import React from 'react';
import { CL_TOKENS, KPI_ICON_KIND, KPI_TONE_COLOR } from '../../receptionist-call-logs/config';
import { KPIS_BY_TAB } from '../../receptionist-call-logs/mockData';
import type { CallLogsKpi, CallLogsTabId } from '../../receptionist-call-logs';
import { Icon } from '../Icon/Icon';

function hexToRgba(hex: string, alpha: number): string {
  const m = hex.replace('#', '').match(/.{1,2}/g);
  if (!m) return `rgba(0,0,0,${alpha})`;
  const [r, g, b] = m.map((x) => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function KpiCard({ kpi }: { kpi: CallLogsKpi }) {
  const accent = kpi.sentiment !== 'neutral';
  const color = KPI_TONE_COLOR[kpi.sentiment];
  const valueColor = accent ? color : 'var(--ink-0)';
  const { delta } = kpi;
  const dp = delta != null && delta > 0;
  const dn = delta != null && delta < 0;
  const valueStr = String(kpi.value);
  const bigFont = typeof kpi.value === 'number' || valueStr.length <= 3 ? 44 : 30;

  return (
    <div
      className="cl-kpi-card relative flex min-h-[132px] flex-col gap-1.5 overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-[17px] pb-4 pt-[15px] shadow-[var(--sh-1)]"
    >
      <div className="relative z-[1] flex items-center gap-2">
        <div
          className="inline-flex h-[23px] w-[23px] flex-none items-center justify-center rounded-[7px]"
          style={{
            background: accent ? hexToRgba(color, 0.12) : CL_TOKENS.surfaceMute,
            color: accent ? color : CL_TOKENS.ink3,
            border: `1px solid ${accent ? hexToRgba(color, 0.22) : CL_TOKENS.border}`,
          }}
        >
          <Icon kind={KPI_ICON_KIND[kpi.icon]} size={12} stroke={1.8} />
        </div>
        <div className="cl-eyebrow min-w-0 flex-1 text-[9.5px]">{kpi.label}</div>
        {delta != null && (
          <span
            className="cl-mono ml-auto inline-flex flex-none items-center gap-1 whitespace-nowrap text-[10.5px] font-semibold"
            style={{ color: dp ? CL_TOKENS.green : dn ? CL_TOKENS.negative : CL_TOKENS.ink4 }}
          >
            <span>
              {dp ? '▲' : dn ? '▼' : '·'}
              {Math.abs(delta)}
            </span>
            <span className="font-medium" style={{ color: CL_TOKENS.ink4 }}>
              vs avg
            </span>
          </span>
        )}
      </div>

      <span
        className="cl-tab-num relative z-[1] mt-1.5 whitespace-nowrap font-semibold leading-[0.95] tracking-[-0.04em]"
        style={{ fontSize: bigFont, color: valueColor }}
      >
        {kpi.value}
      </span>

      <div
        className="relative z-[1] mt-auto pt-1.5 text-[11.5px] font-medium leading-[1.4] tracking-[-0.005em]"
        style={{ color: CL_TOKENS.ink3 }}
      >
        {kpi.sub}
      </div>
    </div>
  );
}

export function CallLogsKpis({ activeTab }: { activeTab: CallLogsTabId }) {
  const kpis = KPIS_BY_TAB[activeTab];
  return (
    <div className="cl-fade-up cl-d-1 mb-3.5 grid grid-cols-4 gap-3">
      {kpis.map((k) => (
        <KpiCard key={k.label} kpi={k} />
      ))}
    </div>
  );
}
